/* globals EasyPost */
import Backbone from 'backbone';
import { setLocation } from 'utils/wrappers';

import Base from 'app/routers/base';

import AccountNavItem from 'app/models/account_nav_item';

import BrandPage from 'app/views/brand_page';
import CarrierActivityMapPage from 'app/views/carrier_activity_map_page';
import WebhooksPage from 'app/views/webhooks_page';
import UserEditPage from 'app/views/user_edit_page';
import CarrierAccountsPage from 'app/views/carrier_accounts_page';
import ReportDetailPage from 'app/views/report_detail_page';
import ApiKeysListView from 'app/views/api_keys_list_view';
import AccountNavView from 'app/views/account_nav_view';

/* new-style imports */
import BillingPage from 'app/components/pages/billing';
import DelayedTrackerPage from 'app/components/pages/delayed_trackers';
import DestinationReliabilityPage from 'app/components/pages/destination_reliability';
import FulfillmentProductsPage from 'app/components/pages/fulfillment_products';
import FulfillmentOrdersPage from 'app/components/pages/fulfillment_orders';
import InsurancesPage from 'app/components/pages/insurances';
import PaymentLogsPage from 'app/components/pages/payment_logs';
import ReportsPage from 'app/components/pages/reports';
import ShipmentsPage from 'app/components/pages/shipments';
import TrackersPage from 'app/components/pages/trackers';

export const ACL_ANALYTICS = 'analytics';
export const ACL_FULFILLMENT = 'fulfillment';

export default Base.extend({
  targetEl: '#account-page',

  routes: {
    account: 'dashboard',
    'account/api-keys': 'apiKeys',
    'account/billing': 'billing',
    'account/brand': 'brand',
    'account/carriers': 'carrierAccounts',
    'account/carrier-activity-map': 'carrierActivityMap',
    'account/delayed-trackers': 'delayedTrackers',
    'account/destination-reliability': 'destinationReliability',
    'account/fulfillment-products': 'fulfillmentProducts',
    'account/fulfillment-orders': 'fulfillmentOrders',
    'account/insurances': 'insurances',
    'account/payment-logs': 'paymentLogs',
    'account/reports/:reportType': 'reports',
    'account/shipments': 'shipments',
    'account/trackers': 'trackers',
    'account/user': 'user',
    'account/webhooks': 'webhooks',
  },

  initialize() {
    const accNavCollection = new Backbone.Collection([
      new AccountNavItem({ route: 'shipments', readable: 'Shipments' }),
      new AccountNavItem({ route: 'trackers', readable: 'Trackers' }),
      new AccountNavItem({ route: 'insurances', readable: 'Insurance' }),
      new AccountNavItem({ route: 'reports/shipment', readable: 'Reports' }),
      new AccountNavItem({ route: 'api-keys', readable: 'API Keys' }),
      new AccountNavItem({ route: 'brand', readable: 'Brand' }),
      new AccountNavItem({ route: 'billing', readable: 'Billing' }),
      new AccountNavItem({ route: 'payment-logs', readable: 'Payment Logs' }),
      new AccountNavItem({ route: 'carriers', readable: 'Carrier Accounts' }),
      new AccountNavItem({ route: 'user', readable: 'User Info' }),
      new AccountNavItem({ route: 'webhooks', readable: 'Webhooks' }),

      new AccountNavItem({ route: '', readable: 'Analytics', aclField: ACL_ANALYTICS }),
      new AccountNavItem({ route: 'delayed-trackers', readable: 'Delayed Trackers', aclField: ACL_ANALYTICS, isSubItem: true }),
      new AccountNavItem({ route: 'carrier-activity-map', readable: 'Carrier Activity Map', aclField: ACL_ANALYTICS, isSubItem: true }),
      new AccountNavItem({ route: 'destination-reliability', readable: 'Destination Reliability', aclField: ACL_ANALYTICS, isSubItem: true }),

      new AccountNavItem({ route: '', readable: 'Fulfillment', aclField: ACL_FULFILLMENT }),
      new AccountNavItem({ route: 'fulfillment-products', readable: 'Inventory', aclField: ACL_FULFILLMENT, isSubItem: true }),
      new AccountNavItem({ route: 'fulfillment-orders', readable: 'Orders', aclField: ACL_FULFILLMENT, isSubItem: true }),
    ]);

    this.sideAccountNav = new AccountNavView({ el: '#side-nav-area', collection: accNavCollection, user: EasyPost.Globals.currentUser });
    this.topAccountNav = new AccountNavView({ el: '#top-nav-area', collection: accNavCollection, user: EasyPost.Globals.currentUser });
  },

  renderAccountPage(ViewClass, options) {
    this.enforceLogin(() => {
      const userACL = EasyPost.Globals.currentUser.get('acl') || {};
      if (options.aclField && !userACL[options.aclField]) {
        this.redirectToLoggedIn();
        return;
      }
      const view = new ViewClass(options);
      const pageName = Backbone.history.getFragment().replace('account/', '');
      this.sideAccountNav.currentPage = pageName;
      this.sideAccountNav.render();
      this.topAccountNav.currentPage = pageName;
      this.topAccountNav.render();
      this.renderView(view);
    });
  },

  dashboard() {
    // check for any hash routes first
    const hash = this.checkForHash(2);
    if (hash.length) {
      setLocation(`account/${hash}`);
    } else {
      this.redirectToLoggedIn();
    }
  },

  apiKeys() {
    this.renderAccountPage(ApiKeysListView, {});
  },

  billing() {
    this.renderAccountPage(BillingPage, { user: EasyPost.Globals.currentUser });
  },

  brand() {
    this.renderAccountPage(BrandPage, { user: EasyPost.Globals.currentUser });
  },

  carrierAccounts() {
    this.renderAccountPage(CarrierAccountsPage, {});
  },

  shipments() {
    this.renderAccountPage(ShipmentsPage, {
      apiMode: EasyPost.Globals.apiMode,
      user: EasyPost.Globals.currentUser,
    });
  },

  trackers() {
    this.renderAccountPage(TrackersPage, { apiMode: EasyPost.Globals.apiMode });
  },

  insurances() {
    this.renderAccountPage(InsurancesPage, { apiMode: EasyPost.Globals.apiMode });
  },

  paymentLogs() {
    this.renderAccountPage(PaymentLogsPage, { user: EasyPost.Globals.currentUser });
  },

  reports(reportType) {
    // we can't have dynamic pages with our static site, so must look for the location hash
    let hash = this.checkForHash(2);
    if (hash.length) {
      hash = hash.replace('mode=', '').split('?');
      // if this url changes, needs to change as well
      this.renderAccountPage(ReportDetailPage, { reportType, reportId: hash[0], mode: hash[1] });
    } else {
      this.renderAccountPage(ReportsPage, {
        apiMode: EasyPost.Globals.apiMode,
        user: EasyPost.Globals.currentUser,
        reportType,
      });
    }
  },

  user() {
    this.renderAccountPage(UserEditPage, { model: EasyPost.Globals.currentUser });
  },

  webhooks() {
    this.renderAccountPage(WebhooksPage, {});
  },

  delayedTrackers() {
    this.renderAccountPage(DelayedTrackerPage, { aclField: ACL_ANALYTICS });
  },

  carrierActivityMap() {
    this.renderAccountPage(CarrierActivityMapPage, { aclField: ACL_ANALYTICS });
  },

  destinationReliability() {
    this.renderAccountPage(DestinationReliabilityPage, { aclField: ACL_ANALYTICS });
  },

  fulfillmentProducts() {
    this.renderAccountPage(FulfillmentProductsPage, {
      model: EasyPost.Globals.apiMode,
      aclField: ACL_FULFILLMENT,
    });
  },

  fulfillmentOrders() {
    this.renderAccountPage(FulfillmentOrdersPage, {
      model: EasyPost.Globals.apiMode,
      aclField: ACL_FULFILLMENT,
    });
  },
});



// WEBPACK FOOTER //
// ./app/routers/account_router.js